import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ButtonControl, DialogControl, Loading } from "spark-core-dx/components";
import { Operations } from "../../../../../services/ItemServices";
import { useResourceDomainContext } from "../../../ResourceDomainProvider";
import {
  getDeploymentCardTitle,
  getDestinationDeploymentRows,
  getDestinationIdFromValues,
  getDestinationLabel,
  getDestinationManagerKey,
  getDestinationSetupDefaults,
  hasLoadedManagerMetadata,
  isCoreProfileMetadataReady,
  loadDestinationById,
  loadDestinationManagerMetadata,
  normalizeDeploymentRow
} from "./destinationSetupStepUtils";
import {
  asArray,
  buildOperationPayload,
  buildResourceOperationQueryParams,
  buildServerFailureError,
  buildWizardValidationError,
  copyTextToClipboard,
  extractValidationIssues,
  getCurrentSectionStepKey,
  getCurrentStepKey,
  getErrorSummary,
  getId,
  getOperationItem,
  getOperationItems,
  getOperationResult,
  getSectionStepKey,
  getStepKey,
  getValidationIssueSummary,
  hasBlockingValidationIssues,
  normalizeValidationSeverity,
  readValue
} from "../resourceConfigWizardUtils";
import { LinkedStepHeader } from "./WizardStepCommon";

const VALIDATION_STATE_FIELD = "__validationState";
const ENDPOINT_READINESS_STATE_FIELD = "__endpointReadinessState";

const getActiveDeployment = (deployments = []) =>
  asArray(deployments).find((deployment) => readValue(deployment, "IsDeleted", "isDeleted") !== true && readValue(deployment, "IsActive", "isActive") !== false)
  || asArray(deployments).find((deployment) => readValue(deployment, "IsDeleted", "isDeleted") !== true)
  || asArray(deployments)[0]
  || null;

const isHostedApiEnabled = (deployment) => {
  const mode = Number(readValue(deployment, "PublicSurfaceMode", "publicSurfaceMode"));
  return mode === 1 || mode === 3;
};

const isMcpEnabled = (deployment) => {
  const mode = Number(readValue(deployment, "PublicSurfaceMode", "publicSurfaceMode"));
  return mode === 2 || mode === 3;
};

const isRouteSafe = (value) => /^[A-Za-z0-9_-]+$/.test(String(value || ""));

const trimTrailingSlash = (value) => String(value || "").replace(/\/+$/, "");

const normalizeReadinessStatus = (status) => {
  const value = String(status || "").trim();
  return value.toLowerCase() === "offline" ? "Success" : value;
};

const getRouteSafeValue = (...values) => asArray(values)
  .map((value) => String(value || "").trim())
  .find((value) => isRouteSafe(value)) || "";

const normalizeSeverityClass = (severity) =>
  normalizeValidationSeverity(severity) === "Warning" ? "warning" : "error";

const issueMessage = (issue) => readValue(issue, "Message", "message", "DisplayMessage", "displayMessage") || "";

const filterIssuesForStep = (issues, stepKey, sectionStepKey) =>
  asArray(issues).filter((issue) =>
    String(readValue(issue, "StepKey", "stepKey") || "").toLowerCase() === String(stepKey || "").toLowerCase()
    && String(readValue(issue, "SectionStepKey", "sectionStepKey") || "").toLowerCase() === String(sectionStepKey || "").toLowerCase());

const extractIssuesForStep = (source, stepKey, sectionStepKey) =>
  filterIssuesForStep(extractValidationIssues(source), stepKey, sectionStepKey);

const getStoredValidationState = (values = {}) => readValue(values, VALIDATION_STATE_FIELD, "validationState") || {};

const getStoredEndpointReadinessState = (values = {}) => readValue(values, ENDPOINT_READINESS_STATE_FIELD, "endpointReadinessState") || {};

const buildValidationStateValues = (values, issues) => ({
  ...(values || {}),
  [VALIDATION_STATE_FIELD]: {
    hasRun: true,
    issues: asArray(issues)
  }
});

const buildEndpointReadinessStateValues = (values, readinessState) => ({
  ...(values || {}),
  [ENDPOINT_READINESS_STATE_FIELD]: readinessState || {}
});

const formatFactValue = (value, emptyText = "Not set") => {
  if (value === true) return "Yes";
  if (value === false) return "No";
  if (Array.isArray(value)) return value.length ? value.join(", ") : emptyText;
  const text = String(value ?? "").trim();
  return text || emptyText;
};

const formatCheckedTime = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return text;
  return date.toLocaleString();
};

const readObject = (source, ...keys) => {
  const value = readValue(source, ...keys);
  return value && typeof value === "object" ? value : {};
};

const summarizeStatusPayload = (payload, response, checkedAtUtc) => {
  const api = readObject(payload, "api", "Api");
  const auth = readObject(payload, "authentication", "Authentication");
  const routes = asArray(readValue(payload, "routes", "Routes"));
  const supportedSchemes = asArray(readValue(auth, "supportedSchemes", "SupportedSchemes"));
  return {
    statusFacts: [
      { label: "HTTP status", value: response?.status ? `${response.status} ${response.statusText || ""}`.trim() : "" },
      { label: "Checked", value: formatCheckedTime(readValue(payload, "checkedAtUtc", "CheckedAtUtc") || checkedAtUtc) },
      { label: "Endpoint online", value: readValue(payload, "endpointOnline", "EndpointOnline") },
      { label: "Hosted API", value: readValue(api, "hostedApiEnabled", "HostedApiEnabled") },
      { label: "MCP", value: readValue(api, "mcpEnabled", "McpEnabled") },
      { label: "Routes", value: routes.length || 0 }
    ],
    authFacts: [
      { label: "Auth mode", value: readValue(auth, "authenticationMode", "AuthenticationMode") },
      { label: "Auth required", value: readValue(auth, "authenticationRequired", "AuthenticationRequired") },
      { label: "Supported schemes", value: supportedSchemes },
      { label: "Anonymous", value: readValue(auth, "anonymousAccessEnabled", "AnonymousAccessEnabled") },
      { label: "Active PAT tokens", value: readValue(auth, "activeApiPatCount", "ActiveApiPatCount") },
      { label: "Basic enabled", value: readValue(auth, "basicAuthEnabled", "BasicAuthEnabled") },
      { label: "Basic client access", value: readValue(auth, "basicClientAccessConfigured", "BasicClientAccessConfigured") },
      { label: "Runtime role", value: readValue(auth, "runtimeRole", "RuntimeRole") },
      { label: "Permission", value: readValue(auth, "endpointPermissionKey", "EndpointPermissionKey") }
    ]
  };
};

const readStatusResponse = async (response) => {
  let payload = null;
  let rawText = "";

  if (typeof response.text === "function") {
    try {
      rawText = await response.text();
    } catch {
      rawText = "";
    }
    if (rawText) {
      try {
        payload = JSON.parse(rawText);
      } catch {
        payload = null;
      }
    }
  } else if (typeof response.json === "function") {
    try {
      payload = await response.json();
      rawText = JSON.stringify(payload);
    } catch {
      payload = null;
    }
  }

  if (!payload) {
    payload = {
      status: response.status,
      statusText: response.statusText,
      body: rawText || ""
    };
  }

  return {
    payload,
    formatted: JSON.stringify(payload, null, 2)
  };
};

export const resolveClientShortName = (endpoint, deployment) => getRouteSafeValue(
  readValue(endpoint, "ClientShortName", "clientShortName"),
  readValue(deployment, "ClientShortName", "clientShortName")
);

const resolveClientId = (endpoint, deployment) =>
  readValue(endpoint, "ClientId", "clientId")
  || readValue(deployment, "ClientId", "clientId")
  || "";

export const resolveClientShortNameAsync = async ({
  endpoint,
  deployment,
  clientMgr
} = {}) => {
  const direct = resolveClientShortName(endpoint, deployment);
  if (direct) return direct;

  const clientId = resolveClientId(endpoint, deployment);
  if (!clientId || !clientMgr) return "";

  try {
    const result = clientMgr.getItem
      ? await clientMgr.getItem(clientId)
      : await clientMgr.Query?.({ model: "x => x.IsDeleted == false && x._id == clientId", params: { clientId } });
    const client = getOperationItem(result) || getOperationItems(result)[0] || null;
    return getRouteSafeValue(
      readValue(client, "ClientShortName", "clientShortName", "ShortName", "shortName", "SystemName", "systemName", "Name", "name")
    );
  } catch {
    return "";
  }
};

const getConfiguredEndpointApiBaseUri = () => {
  const convokeApiUri = window?.configData?.API_URIS?.CONVOKE || "";
  const apiIndex = convokeApiUri.toLowerCase().lastIndexOf("/api/");
  if (apiIndex >= 0) return trimTrailingSlash(convokeApiUri.slice(0, apiIndex));
  return trimTrailingSlash(convokeApiUri);
};

const buildAbsoluteEndpointUrl = (path, deployment) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const deploymentApiBaseUri = deployment ? readValue(deployment, "APIBaseURI", "apiBaseURI", "ApiBaseUri", "apiBaseUri") || "" : "";
  const baseUri = trimTrailingSlash(deploymentApiBaseUri) || getConfiguredEndpointApiBaseUri();
  const normalizedPath = String(path).startsWith("/") ? String(path) : `/${path}`;
  return baseUri ? `${baseUri}${normalizedPath}` : normalizedPath;
};

const normalizeMcpConnectionUrl = (url, deployment) => {
  const absoluteUrl = buildAbsoluteEndpointUrl(url, deployment);
  if (!absoluteUrl) return absoluteUrl;
  return absoluteUrl.replace(/\/test-connect(?=([?#]|$))/i, "");
};

export const buildOpenApiUrl = (endpoint, deployment, clientShortName = "") => {
  if (!isHostedApiEnabled(deployment)) return "";
  const clientRoute = getRouteSafeValue(clientShortName, resolveClientShortName(endpoint, deployment));
  const deploymentKey = readValue(deployment, "Key", "key", "SystemName", "systemName");
  if (!clientRoute || !isRouteSafe(deploymentKey)) return "";
  return buildAbsoluteEndpointUrl(`/api/${clientRoute}/V1/${deploymentKey}/openapi.json`, deployment);
};

export const buildStatusUrl = (endpoint, deployment, clientShortName = "") => {
  const clientRoute = getRouteSafeValue(clientShortName, resolveClientShortName(endpoint, deployment));
  const deploymentKey = readValue(deployment, "Key", "key", "SystemName", "systemName");
  if (!clientRoute || !isRouteSafe(deploymentKey)) return "";
  const surface = isMcpEnabled(deployment) && !isHostedApiEnabled(deployment) ? "mcp" : "api";
  return buildAbsoluteEndpointUrl(`/${surface}/${clientRoute}/V1/${deploymentKey}/status`, deployment);
};

const buildMcpUrl = (deployment) =>
  isMcpEnabled(deployment) ? normalizeMcpConnectionUrl(readValue(deployment, "McpConnectorUrl", "mcpConnectorUrl") || "", deployment) : "";

const EndpointReadinessStep = ({
  context,
  onChange,
  onSave,
  onContinue,
  registerStepController
}) => {
  const { wizard, values = {}, readOnly, saving } = context;
  const resourceDomainContext = useResourceDomainContext();
  const coreContext = resourceDomainContext?.coreContext;
  const coreState = resourceDomainContext?.coreState;
  const productResourceDefaults = useMemo(
    () => getDestinationSetupDefaults(wizard, context.productResourceDefaults),
    [context.productResourceDefaults, wizard]
  );
  const managerKey = getDestinationManagerKey(productResourceDefaults);
  const endpointMgr = resourceDomainContext?.managers?.[managerKey];
  const validationMgr = resourceDomainContext?.managers?.resourceConfigurationWizard;
  const clientMgr = resourceDomainContext?.managers?.client;
  const endpointId = getDestinationIdFromValues(wizard, values, productResourceDefaults);
  const endpointLabel = getDestinationLabel(productResourceDefaults);
  const stepKey = getStepKey(context.step);
  const sectionStepKey = getSectionStepKey(context.sectionStep);
  const storedValidationState = getStoredValidationState(values);
  const initialIssues = filterIssuesForStep(storedValidationState.issues || [], stepKey, sectionStepKey);
  const initialWizardIssues = extractIssuesForStep(wizard, stepKey, sectionStepKey);
  const storedEndpointReadinessState = getStoredEndpointReadinessState(values);
  const [endpoint, setEndpoint] = useState(null);
  const [deployment, setDeployment] = useState(null);
  const [loading, setLoading] = useState(Boolean(endpointId));
  const [checking, setChecking] = useState(false);
  const [localError, setLocalError] = useState("");
  const [statusDialog, setStatusDialog] = useState({ open: false, content: "" });
  const [validationIssues, setValidationIssues] = useState(() => initialIssues.length ? initialIssues : initialWizardIssues);
  const [hasValidated, setHasValidated] = useState(() =>
    Boolean(storedValidationState.hasRun)
    || initialIssues.length > 0
    || initialWizardIssues.length > 0
    || Boolean(storedEndpointReadinessState.hasRun));
  const [endpointReadinessState, setEndpointReadinessState] = useState(() => storedEndpointReadinessState || {});
  const [readiness, setReadiness] = useState(() => ({
    Status: readValue(values, "EndpointReadinessStatus", "endpointReadinessStatus") || "",
    Message: readValue(values, "EndpointReadinessMessage", "endpointReadinessMessage") || "",
    StatusUrl: buildAbsoluteEndpointUrl(readValue(values, "StatusUrl", "statusUrl") || ""),
    OpenApiUrl: "",
    McpConnectorUrl: normalizeMcpConnectionUrl(readValue(values, "McpConnectorUrl", "mcpConnectorUrl") || "")
  }));
  const endpointMgrRef = useRef(endpointMgr);
  const validationMgrRef = useRef(validationMgr);
  const clientMgrRef = useRef(clientMgr);
  const endpointRef = useRef(endpoint);
  const deploymentRef = useRef(deployment);
  const coreStateRef = useRef(coreState);
  const contextRef = useRef(context);
  const valuesRef = useRef(values);
  const productResourceDefaultsRef = useRef(productResourceDefaults);
  const loadKeyRef = useRef("");
  const isBusyRef = useRef(false);
  const readinessRef = useRef(readiness);
  const endpointReadinessStateRef = useRef(endpointReadinessState);

  endpointMgrRef.current = endpointMgr;
  validationMgrRef.current = validationMgr;
  clientMgrRef.current = clientMgr;
  endpointRef.current = endpoint;
  deploymentRef.current = deployment;
  coreStateRef.current = coreState;
  contextRef.current = context;
  valuesRef.current = values;
  productResourceDefaultsRef.current = productResourceDefaults;
  isBusyRef.current = loading || checking;
  endpointReadinessStateRef.current = endpointReadinessState;

  const updateReadiness = useCallback((next) => {
    readinessRef.current = next;
    setReadiness(next);
    onChange?.("EndpointReadinessStatus", next.Status || "");
    onChange?.("EndpointReadinessMessage", next.Message || "");
    onChange?.("StatusUrl", next.StatusUrl || "");
    onChange?.("OpenApiUrl", next.OpenApiUrl || "");
    onChange?.("McpConnectorUrl", next.McpConnectorUrl || "");
  }, [onChange]);

  const updateValidationState = useCallback((issues) => {
    const nextIssues = asArray(issues);
    setValidationIssues(nextIssues);
    setHasValidated(true);
    onChange?.(VALIDATION_STATE_FIELD, {
      hasRun: true,
      issues: nextIssues
    });
  }, [onChange]);

  const updateEndpointReadinessState = useCallback((nextState) => {
    endpointReadinessStateRef.current = nextState || {};
    setEndpointReadinessState(nextState || {});
    onChange?.(ENDPOINT_READINESS_STATE_FIELD, nextState || {});
  }, [onChange]);

  useEffect(() => {
    const nextStoredValidationState = getStoredValidationState(values);
    const nextStoredIssues = filterIssuesForStep(nextStoredValidationState.issues || [], stepKey, sectionStepKey);
    const nextIssues = nextStoredIssues.length ? nextStoredIssues : extractIssuesForStep(wizard, stepKey, sectionStepKey);
    setValidationIssues(nextIssues);
    setHasValidated(
      Boolean(nextStoredValidationState.hasRun)
      || nextIssues.length > 0
      || Boolean(getStoredEndpointReadinessState(values).hasRun)
    );
    const nextEndpointReadinessState = getStoredEndpointReadinessState(values);
    setEndpointReadinessState(nextEndpointReadinessState || {});
    endpointReadinessStateRef.current = nextEndpointReadinessState || {};
  }, [sectionStepKey, stepKey, values, wizard]);

  useEffect(() => {
    let mounted = true;
    const activeMgr = endpointMgrRef.current;
    const loadKey = `${managerKey}:${endpointId || "none"}`;
    if (!endpointId) {
      setEndpoint(null);
      setDeployment(null);
      setLoading(false);
      return undefined;
    }
    if (!activeMgr) {
      setLoading(false);
      setLocalError(`${endpointLabel} service is not available.`);
      return undefined;
    }
    if (!hasLoadedManagerMetadata(activeMgr) && !isCoreProfileMetadataReady(coreContext)) {
      setLoading(true);
      setLocalError("");
      return undefined;
    }
    if (loadKeyRef.current === loadKey) return undefined;
    loadKeyRef.current = loadKey;
    setLoading(true);
    setLocalError("");

    const load = async () => {
      try {
        await loadDestinationManagerMetadata({ manager: activeMgr, coreContext });
        const loadedEndpoint = await loadDestinationById({
          manager: activeMgr,
          destinationId: endpointId,
          coreState: coreStateRef.current,
          fallbackMessage: `${endpointLabel} could not be loaded.`
        });
        if (!mounted) return;
        const deployments = getDestinationDeploymentRows(loadedEndpoint, productResourceDefaultsRef.current)
          .map((row) => normalizeDeploymentRow(row, loadedEndpoint, productResourceDefaultsRef.current));
        const activeDeployment = getActiveDeployment(deployments);
        const clientShortName = await resolveClientShortNameAsync({
          endpoint: loadedEndpoint,
          deployment: activeDeployment,
          coreContext,
          clientMgr: clientMgrRef.current
        });
        setEndpoint(loadedEndpoint);
        setDeployment(activeDeployment);
        const currentReadiness = readinessRef.current || {};
        updateReadiness({
          Status: currentReadiness.Status,
          Message: currentReadiness.Message,
          StatusUrl: buildStatusUrl(loadedEndpoint, activeDeployment, clientShortName),
          OpenApiUrl: "",
          McpConnectorUrl: buildMcpUrl(activeDeployment)
        });
      } catch (ex) {
        if (mounted) setLocalError(ex?.message || `${endpointLabel} readiness could not be loaded.`);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void load();
    return () => {
      mounted = false;
      if (loadKeyRef.current === loadKey) loadKeyRef.current = "";
    };
  }, [coreContext, endpointId, endpointLabel, managerKey, updateReadiness]);

  const runBackendValidation = useCallback(async (valuesToValidate = valuesRef.current) => {
    const currentContext = contextRef.current;
    const manager = validationMgrRef.current;
    if (!manager?.RunOperation) throw new Error("Resource Configuration validation manager is not available.");

    const currentWizard = currentContext.wizard;
    const payload = buildOperationPayload(currentWizard, {
      CurrentStepKey: getCurrentStepKey(currentWizard),
      CurrentSectionStepKey: getCurrentSectionStepKey(currentWizard, getCurrentStepKey(currentWizard)),
      StepKey: getStepKey(currentContext.step),
      SectionStepKey: getSectionStepKey(currentContext.sectionStep),
      StepInputValuesJson: JSON.stringify(valuesToValidate || {})
    });
    const result = await manager.RunOperation({
      operationName: Operations.ResourceConfigurationWizardItem.ResourceConfigurationWizardValidate,
      item: payload,
      queryParams: buildResourceOperationQueryParams({
        wizard: currentWizard,
        item: payload
      }),
      isItem: true,
      isWarning: true,
      supressAllErrors: true
    });
    if (!result) throw new Error("Resource validation did not return a response. Sign in again and retry.");

    const operationResult = getOperationResult(result);
    const failed = readValue(result, "Success", "success") === false
      || readValue(operationResult, "Success", "success") === false;
    const nextIssues = extractIssuesForStep(result, getStepKey(currentContext.step), getSectionStepKey(currentContext.sectionStep));
    if (failed) {
      const error = buildServerFailureError(operationResult || result, "Resource validation failed.", coreStateRef.current);
      if (!error.validationIssues && nextIssues.length) error.validationIssues = nextIssues;
      throw error;
    }
    return nextIssues;
  }, []);

  const runStatusValidation = useCallback(async ({ showDialog = false } = {}) => {
    const currentEndpoint = endpointRef.current;
    const currentDeployment = deploymentRef.current;
    if (!currentEndpoint || !currentDeployment) throw new Error("Save endpoint deployment and scope before validating readiness.");
    const clientShortName = await resolveClientShortNameAsync({
      endpoint: currentEndpoint,
      deployment: currentDeployment,
      coreContext,
      clientMgr: clientMgrRef.current
    });
    const statusUrl = buildStatusUrl(currentEndpoint, currentDeployment, clientShortName);
    if (!statusUrl) throw new Error("Status URL cannot be shown because the endpoint client route key is not available.");

    const checkedAtUtc = new Date().toISOString();
    const response = await fetch(statusUrl, { method: "GET" });
    const { payload, formatted } = await readStatusResponse(response);
    const summary = summarizeStatusPayload(payload, response, checkedAtUtc);
    const nextState = {
      hasRun: true,
      statusOk: Boolean(response.ok),
      statusCode: response.status,
      statusText: response.statusText || "",
      statusUrl,
      checkedAtUtc,
      statusPayload: payload,
      statusPayloadText: formatted,
      statusSummary: summary.statusFacts,
      authenticationSummary: summary.authFacts
    };
    updateEndpointReadinessState(nextState);
    if (showDialog) setStatusDialog({ open: true, content: formatted });

    if (!response.ok) {
      const detail = readValue(payload, "detail", "Detail", "title", "Title") || `Endpoint status returned HTTP ${response.status}.`;
      const error = new Error(detail);
      error.endpointReadinessState = nextState;
      throw error;
    }

    setDeployment(currentDeployment);
    return {
      statusUrl,
      currentEndpoint,
      currentDeployment,
      endpointState: nextState
    };
  }, [coreContext, updateEndpointReadinessState]);

  const runEndpointValidation = useCallback(async ({
    blockOnBackendErrors = false,
    blockOnStatusFailure = false,
    showDialog = false,
    valuesToValidate = valuesRef.current
  } = {}) => {
    isBusyRef.current = true;
    setChecking(true);
    setLocalError("");

    let backendIssues = [];
    let backendFailure = null;
    let statusFailure = null;
    let statusResult = null;

    try {
      try {
        backendIssues = await runBackendValidation(valuesToValidate);
      } catch (ex) {
        backendFailure = ex;
        backendIssues = ex?.validationIssues?.length
          ? ex.validationIssues
          : extractIssuesForStep(ex, stepKey, sectionStepKey);
      }
      updateValidationState(backendIssues);

      try {
        statusResult = await runStatusValidation({ showDialog });
      } catch (ex) {
        statusFailure = ex;
      }

      const hasBackendErrors = hasBlockingValidationIssues(backendIssues);
      const message = statusFailure
        ? getErrorSummary(statusFailure, "Endpoint status validation failed.")
        : hasBackendErrors
          ? getValidationIssueSummary(backendIssues) || getErrorSummary(backendFailure, "Resource validation failed.")
          : "";
      const nextStatus = statusFailure ? "Fail" : "Success";
      const statusUrl = statusResult?.statusUrl
        || readValue(statusFailure?.endpointReadinessState, "statusUrl", "StatusUrl")
        || readinessRef.current?.StatusUrl
        || "";
      const currentEndpoint = statusResult?.currentEndpoint || endpointRef.current;
      const currentDeployment = statusResult?.currentDeployment || deploymentRef.current;
      const next = {
        Status: nextStatus,
        Message: message,
        StatusUrl: statusUrl,
        OpenApiUrl: "",
        McpConnectorUrl: buildMcpUrl(currentDeployment)
      };
      updateReadiness(next);
      if (message) setLocalError(message);

      if ((blockOnStatusFailure && statusFailure) || (blockOnBackendErrors && hasBackendErrors)) {
        const error = buildWizardValidationError(message || "Endpoint validation failed.", { suppressSummary: Boolean(statusFailure) });
        error.validationIssues = backendIssues;
        throw error;
      }

      let nextValues = {
        ...(valuesToValidate || {}),
        DestinationId: getId(currentEndpoint),
        EndpointReadinessStatus: next.Status,
        EndpointReadinessMessage: next.Message,
        StatusUrl: next.StatusUrl,
        OpenApiUrl: next.OpenApiUrl,
        McpConnectorUrl: next.McpConnectorUrl
      };
      nextValues = buildValidationStateValues(nextValues, backendIssues);
      nextValues = buildEndpointReadinessStateValues(nextValues, statusResult?.endpointState || statusFailure?.endpointReadinessState || endpointReadinessStateRef.current);
      return nextValues;
    } finally {
      isBusyRef.current = false;
      setChecking(false);
    }
  }, [runBackendValidation, runStatusValidation, sectionStepKey, stepKey, updateReadiness, updateValidationState]);

  useEffect(() => {
    registerStepController?.({
      skipStepInputValidation: true,
      get isBusy() {
        return Boolean(isBusyRef.current);
      },
      prepareSave: async (nextValues) => runEndpointValidation({
        blockOnBackendErrors: false,
        blockOnStatusFailure: false,
        valuesToValidate: nextValues
      }),
      prepareContinue: async (nextValues) => runEndpointValidation({
        blockOnBackendErrors: true,
        blockOnStatusFailure: true,
        valuesToValidate: nextValues
      })
    });
    return () => registerStepController?.(null);
  }, [registerStepController, runEndpointValidation]);

  const copyValue = useCallback(async (value) => {
    if (!value) return;
    try {
      await copyTextToClipboard(value);
      setLocalError("");
    } catch (ex) {
      setLocalError(getErrorSummary(ex, "Endpoint URL could not be copied."));
    }
  }, []);

  const openValue = useCallback((value) => {
    if (!value) return;
    window.open(value, "_blank", "noopener,noreferrer");
  }, []);

  const renderUrlAbility = (label, value, copyLabel, openLabel) => value ? (
    <div className="resource-wizard__endpoint-url-ability">
      <div className="resource-wizard__endpoint-url-ability-title">
        <span>{label}</span>
        <div className="resource-wizard__endpoint-readiness-actions">
          <ButtonControl
            type="standard-small"
            className="resource-wizard__icon-action"
            aria-label={copyLabel}
            title={copyLabel}
            onClick={() => copyValue(value)}
          >
            <span className="material-symbols-rounded" aria-hidden="true">content_copy</span>
          </ButtonControl>
          <ButtonControl
            type="standard-small"
            className="resource-wizard__icon-action"
            aria-label={openLabel}
            title={openLabel}
            onClick={() => openValue(value)}
          >
            <span className="material-symbols-rounded" aria-hidden="true">open_in_new</span>
          </ButtonControl>
        </div>
      </div>
      <span className="resource-wizard__endpoint-url-value">{value}</span>
    </div>
  ) : null;

  const renderFactGrid = (title, facts, modifier = "") => (
    <section className={`resource-wizard__endpoint-readiness-panel${modifier ? ` resource-wizard__endpoint-readiness-panel--${modifier}` : ""}`}>
      <div className="resource-wizard__endpoint-readiness-panel-title">
        <h3>{title}</h3>
      </div>
      <div className="resource-wizard__pattern-facts resource-wizard__endpoint-readiness-facts">
        {asArray(facts).map((fact) => (
          <div className="resource-wizard__info-pair" key={fact.label}>
            <span>{fact.label}</span>
            <strong>{formatFactValue(fact.value)}</strong>
          </div>
        ))}
      </div>
    </section>
  );

  const renderValidationResults = () => (
    <section className="resource-wizard__endpoint-readiness-panel">
      <div className="resource-wizard__endpoint-readiness-panel-title">
        <h3>Setup Validation</h3>
      </div>
      <div className="resource-wizard__validation-results">
        {validationIssues.length === 0 ? (
          <div className={`resource-wizard__validation-empty${hasValidated ? " resource-wizard__validation-empty--success" : ""}`}>
            {hasValidated ? "Setup validation passed with no backend issues." : "Setup validation has not run yet."}
          </div>
        ) : validationIssues.map((issue, index) => {
          const severityClass = normalizeSeverityClass(readValue(issue, "Severity", "severity"));
          return (
            <div
              className={`resource-wizard__validation-issue resource-wizard__validation-issue--${severityClass}`}
              key={`${severityClass}-${readValue(issue, "Code", "code")}-${issueMessage(issue)}-${index}`}
            >
              <strong>{severityClass === "warning" ? "Warning" : "Error"}</strong>
              <span>{issueMessage(issue)}</span>
            </div>
          );
        })}
      </div>
    </section>
  );

  const warningCount = validationIssues.filter((issue) => normalizeSeverityClass(readValue(issue, "Severity", "severity")) === "warning").length;
  const errorCount = validationIssues.length - warningCount;
  const statusRun = Boolean(endpointReadinessState?.hasRun);
  const statusFailed = statusRun && endpointReadinessState?.statusOk === false;
  const readinessStatus = normalizeReadinessStatus(readiness.Status);
  const readinessStatusFailed = ["fail", "failed"].includes(String(readinessStatus || "").toLowerCase());
  const computedStatusText = checking
    ? "Validating"
    : statusFailed || errorCount > 0 || readinessStatusFailed
      ? "Failed"
      : warningCount > 0
        ? "Warnings"
        : hasValidated && statusRun && endpointReadinessState?.statusOk
          ? "Validated"
          : readinessStatus || "Not checked";
  const readinessStatusText = computedStatusText;
  const readinessMessageText = readiness.Message || "";
  const readinessFailed = readinessStatusFailed || statusFailed || errorCount > 0;
  const displayedError = localError || (readinessFailed ? readinessMessageText : "");
  const missingStatusUrlMessage = !loading && !readiness.StatusUrl
    ? "Status URL cannot be shown because the endpoint client route key is not available."
    : "";
  const showReadinessMessage = readinessMessageText && !readinessFailed;
  const statusSummaryFacts = asArray(endpointReadinessState?.statusSummary);
  const authenticationSummaryFacts = asArray(endpointReadinessState?.authenticationSummary);
  const hasStatusJson = Boolean(endpointReadinessState?.statusPayloadText);
  const statusClass = String(readinessStatusText || "not-checked").toLowerCase().replace(/[^a-z0-9_-]/g, "-");

  return (
    <div className="resource-wizard__linked-step">
      <LinkedStepHeader
        title="Endpoint Readiness"
        description={deployment ? getDeploymentCardTitle(deployment, productResourceDefaults) : `${endpointLabel} validation`}
        actions={(
          <span className={`resource-wizard__readiness-status ${statusClass}`}>
            {readinessStatusText}
          </span>
        )}
      />
      {displayedError ? <div className="resource-wizard__inline-error">{displayedError}</div> : null}
      {loading ? (
        <div className="resource-wizard__linked-step-loading"><Loading /></div>
      ) : (
        <div className="resource-wizard__linked-record-catalog resource-wizard__linked-record-catalog--compact">
          <div className="resource-wizard__review-summary">
            {showReadinessMessage ? (
              <div className={`resource-wizard__connection-status ${String(readinessStatus || "").toLowerCase()}`}>
                {readinessMessageText}
              </div>
            ) : null}
            {missingStatusUrlMessage ? (
              <div className="resource-wizard__connection-status failed">
                {missingStatusUrlMessage}
              </div>
            ) : null}
            {renderUrlAbility("Status URL", readiness.StatusUrl, "Copy Status URL", "Open Status URL")}
            {renderUrlAbility("MCP Connector URL", readiness.McpConnectorUrl, "Copy MCP Connector URL", "Open MCP Connector URL")}
            {statusSummaryFacts.length ? renderFactGrid("Status Response", statusSummaryFacts, "status") : null}
            {authenticationSummaryFacts.length ? renderFactGrid("Authentication Setup", authenticationSummaryFacts, "auth") : null}
            {renderValidationResults()}
            {hasStatusJson ? (
              <div className="resource-wizard__endpoint-readiness-json-action">
                <ButtonControl
                  type="standard-small"
                  className="resource-wizard__button resource-wizard__button--secondary"
                  onClick={() => setStatusDialog({ open: true, content: endpointReadinessState.statusPayloadText || "" })}
                  disabled={checking}
                >
                  View Status JSON
                </ButtonControl>
              </div>
            ) : null}
          </div>
        </div>
      )}
      <div className="resource-wizard__linked-step-actions">
        <div />
        <div className="resource-wizard__linked-step-actions-right">
          <ButtonControl
            type="standard-small"
            className="resource-wizard__button resource-wizard__button--secondary"
            onClick={onSave}
            disabled={readOnly || saving || loading || checking}
            loading={checking}
          >
            Validate
          </ButtonControl>
          <ButtonControl
            type="ok-small"
            className="resource-wizard__button resource-wizard__button--primary"
            onClick={onContinue}
            disabled={readOnly || saving || loading || checking}
            loading={saving || checking}
          >
            Validate & Continue
          </ButtonControl>
        </div>
      </div>
      <DialogControl
        openDialog={statusDialog.open}
        title="Endpoint Status"
        okText="Close"
        cancelText=""
        onOk={() => setStatusDialog({ open: false, content: "" })}
        onCancel={() => setStatusDialog({ open: false, content: "" })}
        disableCancel={true}
      >
        <pre className="resource-wizard__endpoint-status-json">{statusDialog.content}</pre>
      </DialogControl>
    </div>
  );
};

export default EndpointReadinessStep;
